/**
 * Provider Configuration — Iteration 21C-C Phase 2 wired page.
 *
 * Read-only surface exposing the current integration providers as
 * reported by GET /api/security/self-check. Writes are intentionally
 * out of scope for V1.0 (all provider swaps are env-var driven per
 * ADR-0033) so the surface is a machine-readable dashboard rather
 * than a control panel.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

export default function ProviderConfig() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/security/self-check")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, []);

  const providers = data ? [
    { label: "Runtime mode",     value: data.runtime_mode,     mocked: data.runtime_mode !== "production" },
    { label: "KYC provider",     value: data.kyc_provider,     mocked: data.kyc_provider !== "production" && data.kyc_provider !== "live" },
    { label: "Field cipher",     value: data.field_cipher_status,     mocked: data.field_cipher_status !== "active" },
    { label: "MOCK flag",        value: String(data.mock_flag),        mocked: !!data.mock_flag },
    { label: "Demo seed",        value: String(data.demo_seed_enabled),mocked: !!data.demo_seed_enabled },
    { label: "Test credentials", value: String(data.allow_test_credentials), mocked: !!data.allow_test_credentials },
    { label: "Argon2 profile",   value: `t=${data.argon2?.time_cost} m=${data.argon2?.memory_kib}KiB p=${data.argon2?.parallelism}`, mocked: false },
  ] : [];

  return (
    <div className="space-y-4" data-testid="admin-page-providers">
      <h1 className="text-xl font-semibold">Provider Configuration</h1>
      <p className="text-sm text-muted-foreground">
        Current integration providers reported by <code className="text-xs bg-muted px-1 py-0.5 rounded">GET /api/security/self-check</code>.
        All provider swaps are environment-variable driven per ADR-0033 — flip
        <code className="mx-1 text-xs bg-muted px-1 py-0.5 rounded">KYC_PROVIDER</code>,
        <code className="mx-1 text-xs bg-muted px-1 py-0.5 rounded">EMAIL_PROVIDER</code>,
        <code className="mx-1 text-xs bg-muted px-1 py-0.5 rounded">POSTGRES_URL</code>,
        <code className="mx-1 text-xs bg-muted px-1 py-0.5 rounded">REDIS_URL</code> and restart.
      </p>

      {err && (
        <div className="border border-destructive/40 bg-destructive/5 rounded p-3 text-xs">
          Could not load self-check: {typeof err === "string" ? err : JSON.stringify(err)}
        </div>
      )}

      <div className="border rounded overflow-hidden">
        <table className="w-full text-sm" data-testid="providers-table">
          <thead className="bg-muted/30 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Setting</th>
              <th className="text-left p-3">Value</th>
              <th className="text-left p-3">State</th>
            </tr>
          </thead>
          <tbody>
            {providers.map((p) => (
              <tr key={p.label} className="border-t">
                <td className="p-3">{p.label}</td>
                <td className="p-3 font-mono text-xs">{p.value ?? "—"}</td>
                <td className="p-3">
                  {p.mocked ? (
                    <span className="text-xs uppercase tracking-widest px-2 py-0.5 rounded bg-yellow-500/15 text-yellow-700 dark:text-yellow-500">
                      MOCKED / SANDBOX
                    </span>
                  ) : (
                    <span className="text-xs uppercase tracking-widest px-2 py-0.5 rounded bg-green-500/15 text-green-700 dark:text-green-500">
                      PRODUCTION
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
